/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import {fabric} from 'fabric';
import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useFabricImage} from '~/CreateFabricInvitation/hooks/useFabricImage';
import {getEventId} from '~/common/fabric/reducers/eviteEvent/selectors';
import {SET_UPLOADED_IMAGE} from '~/common/fabric/reducers/fabricDesign/constants';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {mexicanOpalWebText} from '~libSass/base/_exports.sass';

/**
 * Hook for handling image upload functionality across the application
 * @param {Object} canvas - Fabric canvas instance
 * @returns {Object} - Object containing upload state and handler function
 */
export const useImageUpload = (canvas) => {
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();
  const eventId = useSelector(getEventId);

  const {
    centerAndScaleImage,
    createImageContainer,
    createImageClipPath,
    findUYOImageById,
    sendRectsToBack,
  } = useFabricImage(canvas);

  /**
   * Handles the image file upload to GCS
   * @param {File} file - The file to upload
   * @returns {Promise<Object>} - The uploaded image details
   */
  const uploadImage = async (file) => {
    if (!file) return null;

    if (file.size > 10 ** 7) {
      toastAlert('Please select an image less than 10MB.', {
        className: 'uyo_toast__error',
        title: 'This image is too large',
        type: 'error',
        icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      throw new Error('Please select an image less than 10MB.');
    }

    const gcsUploadArgs = {
      gcsPathId: GCS_PATH_IDS.anonymous_image,
      params: {
        event_id: eventId,
        image_id: '',
      },
      image: file,
    };

    return gcsUploadImage(gcsUploadArgs);
  };

  /**
   * Sets a new image on the canvas
   * @param {string} url - The image URL
   * @param {Object} options - Configuration options
   */
  const setImageOnCanvas = (url, options) => {
    const {id, callback, isMultipleUyo = false, backgroundImage = {}} = options;

    fabric.UYOImage.fromURL(url, (img) => {
      const uyoImage = findUYOImageById(id);
      if (!uyoImage) return;

      const originalObject = uyoImage._originalObject || uyoImage.toJSON();

      // auto-fit (scale and center) the image
      const image = centerAndScaleImage(img, originalObject, {
        makeSelectable: true,
        resetRotation: true,
      });
      if (!image) return;
      image.set({variant: uyoImage.variant});
      image.setControlsVisibility({
        mtr: false,
        remove: false,
      });

      const rect = canvas
        .getObjects()
        .find((obj) => obj.type === 'rect' && (obj.id === id || obj.id === `${id}-container`));
      if (!rect) {
        // Create a new container for the UYO image if it doesn't already exist
        const newRect = createImageContainer(originalObject, id);
        canvas.insertAt(newRect, 0);
      }

      // Always apply a clipPath to contain the image within the rectangle
      image.clipPath = createImageClipPath(originalObject);

      // Don't cache GIFs to ensure animation works
      if (isMultipleUyo || backgroundImage.src?.endsWith('.gif')) {
        image.objectCaching = false;
      }

      image.set({
        id,
        _originalObject: originalObject,
      });

      const pos = uyoImage.variant === 'logo' ? canvas.getObjects().length : 1; // Insert logo image at the top of the stack
      canvas.remove(uyoImage);
      canvas.insertAt(image, pos);

      canvas.fire('object:added');
      canvas.renderAll();

      sendRectsToBack();

      if (callback) callback(image);
    });
  };

  /**
   * Main handler for image upload process
   * @param {File} file - The file to upload
   * @param {Object} options - Configuration options
   * @returns {Promise<Object|void>} - The uploaded image details or void
   */
  const handleImageUpload = async (file, options = {}) => {
    const {
      id,
      callback,
      resetInputRef = null,
      isMultipleUyo = false,
      backgroundImage = {},
    } = options;

    if (!file) return;

    try {
      setUploading(true);
      const {url, gcsPath, imageKey} = await uploadImage(file);

      setImageOnCanvas(url, {
        id,
        callback,
        isMultipleUyo,
        backgroundImage,
      });

      dispatch({
        type: SET_UPLOADED_IMAGE,
        payload: {url, gcsPath, imageKey},
      });
      return {url, gcsPath, imageKey};
    } catch (error) {
      evite.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
      if (resetInputRef?.current) {
        resetInputRef.current.value = '';
      }
    }
  };

  return {
    uploading,
    handleImageUpload,
  };
};

/**
 * Stand-alone function for image upload to GCS (for backward compatibility)
 * @param {File} file - The file to upload
 * @param {string} eventId - The event ID
 * @returns {Promise<Object>} - The uploaded image details
 */
export const imageFileUpload = async (file, eventId) => {
  if (file.size > 10 ** 7) {
    toastAlert('Please select an image less than 10MB.', {
      className: 'uyo_toast__error',
      title: 'This image is too large',
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    throw new Error('Please select an image less than 10MB.');
  }

  const gcsUploadArgs = {
    gcsPathId: GCS_PATH_IDS.anonymous_image,
    params: {
      event_id: eventId,
      image_id: '',
    },
    image: file,
  };

  return gcsUploadImage(gcsUploadArgs);
};
